import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FiX, FiUser, FiCheck, FiUserPlus, FiCalendar } from 'react-icons/fi';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import './MultiClientAssignmentModal.css';

const MultiClientAssignmentModal = ({ program, onAssign, onClose }) => {
  const { currentUser, userProfile } = useAuth();
  const [clients, setClients] = useState([]);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [clientStartDates, setClientStartDates] = useState({}); // Map of clientId -> start date
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const usersRef = collection(db, 'users');
      const clientsQuery = query(
        usersRef,
        where('trainerId', '==', currentUser.uid),
        where('role', '==', 'client')
      );

      const querySnapshot = await getDocs(clientsQuery);
      const clientsData = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        clientsData.push({
          id: doc.id,
          name: data.displayName || 'Unnamed Client',
          email: data.email
        });
      });

      setClients(clientsData);
    } catch (error) {
      console.error('[MultiClientAssignmentModal] Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleClientSelection = (clientId) => {
    setSelectedClientIds(prev => {
      if (prev.includes(clientId)) {
        // Remove client and their start date
        setClientStartDates(prevDates => {
          const newDates = { ...prevDates };
          delete newDates[clientId];
          return newDates;
        });
        return prev.filter(id => id !== clientId);
      } else {
        // Add client with default start date (today)
        setClientStartDates(prevDates => ({
          ...prevDates,
          [clientId]: new Date().toISOString().split('T')[0] // YYYY-MM-DD format
        }));
        return [...prev, clientId];
      }
    });
  };

  const handleStartDateChange = (clientId, date) => {
    setClientStartDates(prev => ({
      ...prev,
      [clientId]: date
    }));
  };

  const selectAll = () => {
    const allClientIds = clients.map(c => c.id);
    setSelectedClientIds(allClientIds);

    // Set default start dates for all clients
    const defaultDates = {};
    allClientIds.forEach(id => {
      defaultDates[id] = new Date().toISOString().split('T')[0];
    });
    setClientStartDates(defaultDates);
  };

  const deselectAll = () => {
    setSelectedClientIds([]);
    setClientStartDates({});
  };

  const handleAssign = () => {
    if (selectedClientIds.length === 0) {
      alert('Please select at least one client');
      return;
    }

    // Check if all selected clients have start dates
    const missingDates = selectedClientIds.filter(id => !clientStartDates[id]);
    if (missingDates.length > 0) {
      alert('Please select a start date for all selected clients');
      return;
    }

    const selectedClients = clients.filter(c => selectedClientIds.includes(c.id)).map(client => ({
      ...client,
      startDate: clientStartDates[client.id]
    }));

    onAssign(selectedClients);
  };

  const modalContent = (
    <div className="multi-client-assignment-modal">
      <div className="modal-overlay" onClick={onClose}></div>
      <div className="modal-content">
        <div className="modal-header">
          <h2>Assign Program to Clients</h2>
        </div>

        <div className="modal-body">
          <div className="program-info">
            <h3>{program.name || program.metadata?.name || 'Unnamed Program'}</h3>
            <p className="program-details">
              {program.numberOfCycles || program.metadata?.duration?.weeks || 0} cycles • {program.sessionsPerCycle || program.metadata?.duration?.sessionsPerWeek || 0} sessions per cycle
            </p>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading clients...</p>
            </div>
          ) : clients.length === 0 ? (
            <div className="empty-state">
              <FiUser />
              <p>You don't have any clients yet.</p>
              <small>Add clients from the Clients page to assign them programs.</small>
            </div>
          ) : (
            <>
              <div className="selection-header">
                <h4>Select clients ({selectedClientIds.length} selected):</h4>
                <div className="selection-actions">
                  <button className="select-action-btn" onClick={selectAll}>
                    Select All
                  </button>
                  <button className="select-action-btn" onClick={deselectAll}>
                    Deselect All
                  </button>
                </div>
              </div>

              <div className="clients-list">
                {clients.map(client => (
                  <div
                    key={client.id}
                    className={`client-checkbox-card ${selectedClientIds.includes(client.id) ? 'selected' : ''}`}
                    onClick={() => toggleClientSelection(client.id)}
                  >
                    <div className="checkbox-container">
                      <input
                        type="checkbox"
                        checked={selectedClientIds.includes(client.id)}
                        onChange={() => {}} // Handled by parent div onClick
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="custom-checkbox">
                        {selectedClientIds.includes(client.id) && <FiCheck />}
                      </span>
                    </div>

                    <div className="client-info">
                      <span className="client-name">{client.name}</span>
                      {selectedClientIds.includes(client.id) && (
                        <div className="client-start-date">
                          <FiCalendar className="calendar-icon" />
                          <input
                            type="date"
                            value={clientStartDates[client.id] || ''}
                            onChange={(e) => handleStartDateChange(client.id, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="date-input"
                            min={new Date().toISOString().split('T')[0]}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="assign-btn"
            onClick={handleAssign}
            disabled={selectedClientIds.length === 0}
          >
            <FiUserPlus /> Assign to {selectedClientIds.length} Client{selectedClientIds.length !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default MultiClientAssignmentModal;
